import type { WorkerRecord } from '@dyspatch/core';

/**
 * Presence as the console draws it, and the heartbeat cut-off it shares with the
 * scheduler. Its own module for the same reason `event-kinds.ts` is one: the
 * number below is mirrored from core rather than imported, so a test has to be
 * able to load it, and a test can only load a file that stays clear of the app's
 * browser-only imports.
 */

/** Display presence, which is not core's `presenceOf`. */
export type Presence = 'busy' | 'idle' | 'paused' | 'offline';

/**
 * Core's `heartbeatTimeoutMs`, mirrored. Importing `DEFAULT_SCHEDULER_CONFIG`
 * would be a *value* import of `@dyspatch/core`, whose barrel pulls in `node:fs`
 * and `node:crypto` -- into a browser bundle. Same trade as `event-kinds.ts`,
 * and the same safety net: `packages/hub/test/web.test.ts` fails the build if
 * this number and core's ever disagree.
 */
export const HEARTBEAT_TIMEOUT_MS = 120_000;

/**
 * Core's `presenceOf` answers a scheduling question (online / cooldown / dnd /
 * offline) and needs a `SchedulerConfig` the browser does not have. The strip
 * asks a different one: is this machine *doing something right now*.
 *
 * Heartbeat freshness is checked first, and that order is the whole correctness
 * of this function. A worker whose heartbeat has aged out is offline however
 * many tasks its rows still claim: a closed laptop leaves its `running` task
 * behind, and answering `busy` for it painted a green live cell on Home and a
 * "busy" pill on /fleet for a machine the scheduler was already refusing to
 * offer work to -- while /me and /people, which ask the server, said offline.
 * Same cut-off as core's `isOnline`, so a worker does not change colour
 * depending on which page you opened.
 */
export function presenceOf(worker: WorkerRecord, now = Date.now()): Presence {
  const last = worker.lastHeartbeatAt ? Date.parse(worker.lastHeartbeatAt) : Number.NaN;
  if (Number.isNaN(last) || now - last > HEARTBEAT_TIMEOUT_MS) return 'offline';
  if (worker.activeTaskCount > 0) return 'busy';
  if (worker.dnd) return 'paused';
  return 'idle';
}

/**
 * The same four words for one of *my* devices, whose presence arrives from the
 * server already decided.
 *
 * `/api/me/devices` reports core's `presenceOf`, whose vocabulary is
 * `online` / `cooldown` / `dnd` / `offline`. The device card used to compare
 * that against `busy` and `idle`, two words the server never sends, so every
 * one of a person's own laptops fell through to the last branch and was drawn
 * "Offline" a line above "heard from just now". The fleet table beside it, which
 * derives presence from the heartbeat itself, said "idle" about the same
 * machine. This is the one translation, so the two readings cannot disagree.
 *
 * Cooldown collapses into `idle`: the device is awake and free, and the
 * scheduler's short backoff after a limit error is not a state a person needs
 * a fifth word for on this page.
 */
export function devicePresenceOf(device: {
  presence: string;
  dnd: boolean;
  activeTaskCount: number;
}): Presence {
  if (device.presence === 'offline') return 'offline';
  if (device.activeTaskCount > 0) return 'busy';
  if (device.dnd || device.presence === 'dnd') return 'paused';
  return 'idle';
}

/** The word a person reads for each presence, capitalised as a label. */
export const PRESENCE_LABEL: Record<Presence, string> = {
  busy: 'Working',
  idle: 'Ready',
  paused: 'Paused',
  offline: 'Offline',
};
